import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Send, CheckCircle2, AlertCircle, CalendarCheck, HelpCircle, LifeBuoy } from "lucide-react";
import { useContactDialog } from "../context/ContactDialogContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_TYPES = [
  {
    value: "demo",
    label: "Boka Demo",
    description: "Boka en kostnadsfri online-demo av plattformen.",
    icon: CalendarCheck,
    subject: "Förfrågan om online-demo",
  },
  {
    value: "info",
    label: "Vill veta mer / Frågor",
    description: "Generella frågor om Orkesteradmin eller priser.",
    icon: HelpCircle,
    subject: "Vill veta mer om Orkesteradmin",
  },
  {
    value: "support",
    label: "Support",
    description: "Du är kund och behöver hjälp eller har en fråga.",
    icon: LifeBuoy,
    subject: "Supportförfrågan",
  },
];

const initialState = {
  name: "",
  email: "",
  organization: "",
  phone: "",
  contact_type: "",
  subject: "",
  message: "",
};

const TextField = ({ name, label, type = "text", required, placeholder, autoComplete, value, onChange }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] text-white/55 mb-2">
      {label} {required && <span className="text-[#d4af37]">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      autoComplete={autoComplete}
      placeholder={placeholder}
      data-testid={`contact-input-${name}`}
      className="w-full bg-[#0a1128]/60 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 outline-none focus:border-[#d4af37]/60 focus:ring-2 focus:ring-[#d4af37]/30 transition"
    />
  </label>
);

const ContactDialog = () => {
  const { open, intent, setOpen, closeDialog } = useContactDialog();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setResult(null);
      const presetType =
        intent === "demo" ? "demo" : intent === "pricing" ? "info" : "";
      const preset = CONTACT_TYPES.find((t) => t.value === presetType);
      setForm({
        ...initialState,
        contact_type: presetType,
        subject: preset?.subject || "",
      });
    }
  }, [open, intent]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const selectContactType = (value) => {
    const preset = CONTACT_TYPES.find((t) => t.value === value);
    setForm((f) => ({
      ...f,
      contact_type: value,
      subject:
        !f.subject || CONTACT_TYPES.some((t) => t.subject === f.subject)
          ? preset?.subject || ""
          : f.subject,
    }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Vänligen ange ditt namn.";
    if (!form.email.trim()) return "Vänligen ange din e-postadress.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Vänligen ange en giltig e-postadress.";
    if (!form.contact_type) return "Vänligen välj typ av kontakt.";
    if (!form.subject.trim()) return "Vänligen ange ett ämne.";
    if (!form.message.trim()) return "Vänligen skriv ett meddelande.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setResult({ ok: false, message: err });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setResult({
        ok: true,
        message:
          res.data?.message ||
          "Tack! Ditt meddelande är mottaget. Vi återkommer så snart vi kan.",
      });
      setForm(initialState);
      // Stäng dialogen och skicka besökaren till startsidan efter en kort stund
      setTimeout(() => {
        closeDialog();
        navigate("/");
      }, 1600);
    } catch (e2) {
      setResult({
        ok: false,
        message:
          e2?.response?.data?.detail ||
          "Något gick fel. Försök igen om en stund.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="contact-dialog"
        className="max-w-2xl bg-[#0a1128] border border-white/10 text-white p-0 overflow-hidden"
      >
        <div className="relative">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#d4af37]/10 blur-3xl rounded-full pointer-events-none" />
          <div className="relative p-8 md:p-10 max-h-[85vh] overflow-y-auto">
            <DialogHeader className="text-left">
              <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37]">
                Kontakt
              </span>
              <DialogTitle className="font-serif text-3xl md:text-4xl text-white mt-3 leading-tight">
                Boka demo eller <span className="italic gold-text">ställ en fråga.</span>
              </DialogTitle>
              <DialogDescription className="text-white/65 mt-3">
                Skriv några rader så återkommer vi inom kort.
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              className="mt-8 space-y-5"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <TextField name="name" label="Namn" required autoComplete="name" placeholder="För- och efternamn" value={form.name} onChange={handleChange} />
                <TextField name="email" label="E-post" type="email" required autoComplete="email" placeholder="namn@exempel.se" value={form.email} onChange={handleChange} />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <TextField name="organization" label="Orkester / Organisation" autoComplete="organization" placeholder="t.ex. Storstadens Symfoniorkester" value={form.organization} onChange={handleChange} />
                <TextField name="phone" label="Telefon" type="tel" autoComplete="tel" placeholder="Valfritt" value={form.phone} onChange={handleChange} />
              </div>

              <div data-testid="contact-type-list">
                <span className="block text-xs uppercase tracking-[0.18em] text-white/55 mb-3">
                  Typ av kontakt <span className="text-[#d4af37]">*</span>
                </span>
                <ul className="space-y-2.5" role="radiogroup" aria-label="Typ av kontakt">
                  {CONTACT_TYPES.map((opt) => {
                    const Icon = opt.icon;
                    const selected = form.contact_type === opt.value;
                    return (
                      <li key={opt.value}>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => selectContactType(opt.value)}
                          data-testid={`contact-type-${opt.value}`}
                          className={`w-full flex items-start gap-4 text-left rounded-lg border px-4 py-3.5 transition-all ${
                            selected
                              ? "border-[#d4af37] bg-[#d4af37]/10"
                              : "border-white/10 bg-[#0a1128]/40 hover:border-white/25"
                          }`}
                        >
                          <span
                            className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                              selected ? "border-[#d4af37]" : "border-white/30"
                            }`}
                          >
                            {selected && (
                              <span className="h-2.5 w-2.5 rounded-full bg-[#d4af37]" />
                            )}
                          </span>
                          <Icon
                            className={`w-5 h-5 shrink-0 mt-0.5 ${selected ? "text-[#d4af37]" : "text-white/60"}`}
                            strokeWidth={1.6}
                          />
                          <span className="flex-1">
                            <span className={`block text-sm font-medium ${selected ? "text-white" : "text-white/85"}`}>
                              {opt.label}
                            </span>
                            <span className="block text-xs text-white/55 mt-0.5">
                              {opt.description}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <TextField name="subject" label="Ämne" required placeholder="Vad gäller din förfrågan?" value={form.subject} onChange={handleChange} />

              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-white/55 mb-2">
                  Meddelande <span className="text-[#d4af37]">*</span>
                </span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Berätta gärna lite om er orkester och vad ni är nyfikna på."
                  data-testid="contact-input-message"
                  className="w-full bg-[#0a1128]/60 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 outline-none focus:border-[#d4af37]/60 focus:ring-2 focus:ring-[#d4af37]/30 transition resize-y"
                />
              </label>

              {result && (
                <div
                  data-testid={result.ok ? "contact-success-message" : "contact-error-message"}
                  className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
                    result.ok
                      ? "border-[#d4af37]/40 bg-[#d4af37]/5 text-[#f1d977]"
                      : "border-red-500/40 bg-red-500/10 text-red-200"
                  }`}
                >
                  {result.ok ? (
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  )}
                  <span>{result.message}</span>
                </div>
              )}

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDialog}
                  data-testid="contact-dialog-close-button"
                  className="px-6 py-3 rounded-full text-white/70 hover:text-white text-sm transition"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Skickar..." : (
                    <>
                      Skicka meddelande <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
